// Pure, testable frontend entitlement decisions (defense-in-depth).
// These never grant access — they only decide what a given plan may RENDER,
// mirroring the backend rule that LIVE + actionable prematch recommendations
// require PRO+ (FREE is discovery-only). The authoritative plan comes from
// useLaunch().hasPlan(...); these helpers just encode the render policy so it
// can be unit-tested without a DOM.
import { lifecycleOf } from "./resultPresentation";
import { fixtureKeys } from "./fixtureIdentity";

// FREE must NOT render a CURRENT (pending) prematch recommendation; resolved
// historical cards remain visible to everyone.
export function lockPrematchPick(canSeePicks: boolean, status: unknown, won: unknown): boolean {
  if (canSeePicks) return false;
  return lifecycleOf(status as any, won as any) === "PENDING";
}

// FREE "Son Hareketler" / feed: drop CURRENT (pending) live/prematch rows that
// would leak an active team/market/recommendation. Keep resolved history.
export function visibleFeedRows<T extends { status: unknown; won: unknown }>(rows: T[], canSeePicks: boolean): T[] {
  if (canSeePicks) return rows;
  return rows.filter((r) => lifecycleOf(r.status as any, r.won as any) !== "PENDING");
}

// FREE history: resolved historical rows only (no PENDING/current live preview).
export function visibleHistoryRows<T extends { status: unknown; won: unknown }>(rows: T[], canSeePicks: boolean): T[] {
  return visibleFeedRows(rows, canSeePicks);
}

// Canonical CUSTOMER-LIVE identity: see canonicalLiveEventKeys / inCanonicalLiveEvent
// below (EVENT-level — fixture_id alone is intentionally NOT used).
function normMarket(code: unknown): string {
  return String(code ?? "").trim().toUpperCase().replace(/\s+/g, "_");
}

type LiveRec = { market_code?: unknown; event_id?: unknown; signal_minute?: unknown; minute?: unknown };
type LiveFx = { keys?: string[]; fixture_id: string | number; primary?: LiveRec | null; alternatives?: LiveRec[]; watching?: LiveRec[] };

// Canonical EVENT-level identity keys from Market Hub live fixtures. A fixture
// can carry multiple distinct production events (e.g. OVER_1_5 and ONE_MORE_GOAL);
// each recommendation contributes a key at the STRONGEST identity available:
//   evt:<event_id> (when both sides expose it)  >  fms:<fixture>|<market>|<minute>
// so a stale raw signal cannot render just because ANOTHER event on the same
// fixture is canonical.
export function canonicalLiveEventKeys(liveFixtures: LiveFx[] | undefined | null): Set<string> {
  const set = new Set<string>();
  for (const f of liveFixtures || []) {
    const fids = f.keys && f.keys.length ? f.keys : [String(f.fixture_id)];
    const recs = [f.primary, ...(f.alternatives || []), ...(f.watching || [])].filter(Boolean) as LiveRec[];
    for (const r of recs) {
      const m = normMarket(r.market_code);
      if (!m) continue;
      if (r.event_id != null) set.add(`evt:${String(r.event_id)}`);
      const minute = r.signal_minute ?? r.minute;
      if (minute == null) continue;
      for (const fid of fids) {
        set.add(`fms:${fid}|${m}|${minute}`);
        if (r.event_id != null) set.add(`fmse:${fid}|${m}|${minute}`);
      }
    }
  }
  return set;
}

// EVENT-level membership for a raw LIVE signal. Matches on production event id
// when both sides expose it; otherwise fixture+market+signal_minute. Never
// fixture_id alone, never fixture+market without minute.
export function inCanonicalLiveEvent(
  signal: { id?: unknown; fixture_id?: unknown; market_code?: unknown; minute?: unknown; signal_minute?: unknown } | null | undefined,
  canonicalKeys: Set<string>,
): boolean {
  if (!signal) return false;
  if (signal.id != null && canonicalKeys.has(`evt:${String(signal.id)}`)) return true;
  const m = normMarket(signal.market_code);
  const fids = fixtureKeys(signal);
  const minute = signal.minute ?? signal.signal_minute;
  if (minute == null || !m) return false;
  for (const fid of fids) {
    if (!canonicalKeys.has(`fms:${fid}|${m}|${minute}`)) continue;
    // Both sides carry an event id but they differ -> a different event.
    if (signal.id != null && canonicalKeys.has(`fmse:${fid}|${m}|${minute}`)) continue;
    return true;
  }
  return false;
}
